import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Chip, Button, Link, Stack, Typography, TextField, MenuItem, Alert,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
} from "@mui/material";

const label = (s) => String(s || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// The officer's first duty (no notifications yet) is to VERIFY the students who
// marked interested — so the list defaults to that status.
const DEFAULT_STATUS = "interested";

// Reusable applicants table (status filter dropdown + rows + unread "Updates").
// Used by the per-job Applicants page and the sidebar Applicants page.
export default function ApplicantsTable({ rows = [], counts = {}, loading = false }) {
  const navigate = useNavigate();
  // active status filter — defaults to "interested" (needs verification); "all" shows everyone
  const [filter, setFilter] = useState(DEFAULT_STATUS);

  const statuses = Object.keys(counts).filter((k) => k !== "total");
  // applicants with new updates (unread messages/history) — across ALL statuses,
  // so an update on a non-"interested" applicant is never hidden by the filter
  const withUpdates = rows.filter((r) => Number(r.unread_count) > 0);

  // On first load: if this job HAS updates, default to the "New updates" view so it
  // matches the "N new" badge the officer clicked from on the jobs page (otherwise a
  // verified applicant's update looks hidden by the default "interested" filter).
  // If there are no updates, keep the "interested" verify queue.
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current || rows.length === 0) return;
    initialized.current = true;
    if (rows.some((r) => Number(r.unread_count) > 0)) setFilter("updates");
  }, [rows]);

  // "updates" and "all" are always valid; a status filter falls back to "all"
  // if this job has none of it
  const activeFilter =
    filter === "all" || filter === "updates" || statuses.includes(filter) ? filter : "all";
  const visibleRows =
    activeFilter === "all"
      ? rows
      : activeFilter === "updates"
      ? withUpdates
      : rows.filter((r) => r.application_status === activeFilter);

  return (
    <>
      {/* surface new updates regardless of the current status filter */}
      {withUpdates.length > 0 && activeFilter !== "updates" && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setFilter("updates")}>
              Show
            </Button>
          }
        >
          {withUpdates.length} applicant{withUpdates.length > 1 ? "s have" : " has"} new updates
        </Alert>
      )}

      {/* dropdown status filter (defaults to "interested" = to verify).
          A dropdown scales as the managed status list grows. */}
      <TextField
        select
        size="small"
        fullWidth
        label="Filter by status"
        value={activeFilter}
        onChange={(e) => setFilter(e.target.value)}
        sx={{ maxWidth: { xs: "100%", sm: 320 }, mb: 2 }}
      >
        <MenuItem value="all">All ({counts.total || 0})</MenuItem>
        {withUpdates.length > 0 && (
          <MenuItem value="updates">New updates ({withUpdates.length})</MenuItem>
        )}
        {statuses.map((k) => (
          <MenuItem key={k} value={k}>{label(k)} ({counts[k]})</MenuItem>
        ))}
      </TextField>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Student</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Resume</TableCell>
              <TableCell>Applied</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updates</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {visibleRows.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  {loading
                    ? "Loading…"
                    : rows.length === 0
                    ? "No applicants yet."
                    : `No applicants in "${label(activeFilter)}".`}
                </TableCell>
              </TableRow>
            )}
            {visibleRows.map((r) => (
              <TableRow key={r.career_application_id} hover>
                <TableCell>
                  <Typography variant="body2" fontWeight={600}>
                    {r.student_name || `Admn ${r.admn_no}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Admn {r.admn_no}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{r.email || ""}</Typography>
                  <Typography variant="caption" color="text.secondary">{r.student_mob_no || ""}</Typography>
                </TableCell>
                <TableCell>
                  {r.resume_link ? (
                    <Link href={r.resume_link} target="_blank" rel="noopener">Open resume</Link>
                  ) : ("")}
                </TableCell>
                <TableCell>{(r.applied_at || "").slice(0, 10)}</TableCell>
                <TableCell><Chip size="small" label={label(r.application_status)} /></TableCell>
                <TableCell>
                  {Number(r.unread_count) > 0 ? (
                    <Chip size="small" color="error" label={`${r.unread_count} new`} />
                  ) : (
                    <span style={{ color: "#9aa5b1" }}></span>
                  )}
                </TableCell>
                <TableCell align="right">
                  <Button size="small" onClick={() => navigate(`/career-connect/applications/${r.career_application_id}`)}>
                    View
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}
